import { AlertCircle } from 'lucide-react'

import { useReport } from '@/hooks/features/reports'
import { useBusiness } from '@/contexts/BusinessContext'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

import { PageHeader } from '@/components/atoms'
import { StatCard } from '@/components/dashboard/StatCard'

import type { InventoryReport as InventoryReportData } from '@/types/reports'

/** Stock right now across all branches: units, value at cost and at price, and what is low or out of stock. */
const InventoryReport = () => {
  const { formatCurrency } = useBusiness()
  const report = useReport<InventoryReportData>('inventory', { limit: 100 })
  const r = report.data

  return (
    <div className="space-y-4">
      <PageHeader title="Inventory report" description="Stock on hand right now, across all branches." />

      {report.isError && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Could not load the report</AlertTitle>
          <AlertDescription>{report.error.message}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {r ? (
          <>
            <StatCard title="Stock value (cost)" value={formatCurrency(r.stock_value)} change={0} trend="neutral" note={`${formatCurrency(r.retail_value)} at selling price`} />
            <StatCard title="Units on hand" value={r.units.toLocaleString()} change={0} trend="neutral" note={`${r.variations.toLocaleString()} variations`} />
            <StatCard title="Low stock" value={r.low_stock.toLocaleString()} change={0} trend="neutral" note="At or below their minimum" />
            <StatCard title="Out of stock" value={r.out_of_stock.toLocaleString()} change={0} trend="neutral" note="Can't be sold now" />
          </>
        ) : (
          Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-24" />)
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="w-fit text-sm font-semibold underline decoration-faint decoration-dotted underline-offset-4">Needs restocking</CardTitle>
        </CardHeader>
        <CardContent>
          {!r ? (
            <Skeleton className="h-48" />
          ) : !r.attention.length ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Everything is above its minimum stock.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead className="text-right">On hand</TableHead>
                  <TableHead className="text-right">Minimum</TableHead>
                  <TableHead>State</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {r.attention.map((v) => (
                  <TableRow key={v.variation_id}>
                    <TableCell className="font-medium">{v.name || 'Unnamed'}</TableCell>
                    <TableCell className="text-muted-foreground">{v.sku || '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">{v.on_hand}</TableCell>
                    <TableCell className="text-right tabular-nums">{v.min_stock}</TableCell>
                    <TableCell>{v.on_hand <= 0 ? <Badge tone="error">Out of stock</Badge> : <Badge tone="warning">Low</Badge>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default InventoryReport
